import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check } from 'lucide-react'
import { Card, CardContent, CardFooter } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Skeleton } from '@/common/ui/skeleton'
import { Separator } from '@/common/ui/separator'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import Heading from '@/common/shared/heading'
import { cn } from '@/lib/utils'
import useQueryGetThemePresets from '@/modules/client-setting/api/hooks/useQueryGetThemePresets'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import useMutationApplyThemePreset from '@/modules/client-setting/api/hooks/useMutationApplyThemePreset'
import { GeneralSettingQueryKeys } from '@/modules/client-setting/keys'
import type { ThemePresetData } from '@/modules/client-setting/type'

export default function ThemePresetSelector() {
	const queryClient = useQueryClient()
	const { data: presetsData, isLoading: presetsLoading } =
		useQueryGetThemePresets()
	const { data: generalSetting } = useQueryGetGeneralSetting()
	const [applyingId, setApplyingId] = useState<string | null>(null)
	const [pendingPreset, setPendingPreset] = useState<ThemePresetData | null>(
		null,
	)

	const { mutateAsync } = useMutationApplyThemePreset()

	const activePresetId = generalSetting?.themePreset ?? null

	const handlePresetClick = (preset: ThemePresetData) => {
		if (preset.id === activePresetId) return
		setPendingPreset(preset)
	}

	const handleConfirmApply = async () => {
		if (!pendingPreset) return
		const preset = pendingPreset
		setPendingPreset(null)
		setApplyingId(preset.id)
		try {
			await mutateAsync({ presetId: preset.id })
			await queryClient.invalidateQueries({
				queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
			})
			toast.success(`เปลี่ยนธีมเป็น "${preset.name}" สำเร็จ`)
		} catch {
			toast.error('ไม่สามารถเปลี่ยนธีมได้')
		} finally {
			setApplyingId(null)
		}
	}

	const presets = presetsData?.data ?? []

	return (
		<div className="space-y-4">
			<Heading
				title="ธีมสีเว็บไซต์"
				description="เลือกธีมสีสำหรับเว็บไซต์ผู้เล่น ระบบจะเปลี่ยน CSS และโหมดมืด/สว่างอัตโนมัติ"
			/>
			<Separator />

			{presetsLoading ? (
				<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{Array.from({ length: 6 }).map((_, i) => (
						<ThemePresetSkeleton key={i} />
					))}
				</div>
			) : presets.length === 0 ? (
				<div className="flex h-32 items-center justify-center">
					<p className="text-muted-foreground">ไม่พบธีมพรีเซ็ต</p>
				</div>
			) : (
				<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{presets.map((preset) => {
						const isActive = preset.id === activePresetId
						const isApplying = preset.id === applyingId

						return (
							<Card
								key={preset.id}
								className={cn(
									'group relative flex flex-col overflow-hidden transition-all hover:shadow-md',
									isActive && 'ring-2 ring-primary',
									isApplying && 'pointer-events-none opacity-70',
								)}
							>
								<div className="aspect-[3/4] w-full overflow-hidden bg-muted">
									{preset.thumbnailUrl ? (
										<img
											src={preset.thumbnailUrl}
											alt={preset.name}
											className="h-full w-full object-contain"
										/>
									) : (
										<div className="flex h-full w-full items-center justify-center">
											<span className="text-xs text-muted-foreground">
												ไม่มีรูปตัวอย่าง
											</span>
										</div>
									)}

									{isActive && (
										<div className="absolute right-2 top-2 rounded-full bg-primary p-1 text-primary-foreground shadow">
											<Check className="h-3.5 w-3.5" />
										</div>
									)}
								</div>

								<CardContent className="p-3">
									<div className="flex items-center justify-between gap-2">
										<span className="truncate text-sm font-medium">
											{preset.name}
										</span>
										<Badge
											variant={
												preset.themeMode === 'dark' ? 'default' : 'outline'
											}
											className="shrink-0 text-xs"
										>
											{preset.themeMode === 'dark' ? 'มืด' : 'สว่าง'}
										</Badge>
									</div>
								</CardContent>

								<CardFooter className="p-3 pt-0">
									<Button
										className="w-full"
										variant={isActive ? 'outline' : 'default'}
										disabled={isActive || isApplying}
										onClick={() => handlePresetClick(preset)}
									>
										{isApplying
											? 'กำลังเปลี่ยน...'
											: isActive
												? 'ใช้งานอยู่'
												: 'ใช้ธีมนี้'}
									</Button>
								</CardFooter>
							</Card>
						)
					})}
				</div>
			)}

			<AlertDialog
				open={!!pendingPreset}
				onOpenChange={(open) => {
					if (!open) setPendingPreset(null)
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันเปลี่ยนธีม</AlertDialogTitle>
						<AlertDialogDescription>
							ต้องการเปลี่ยนธีมเว็บไซต์เป็น &quot;{pendingPreset?.name}
							&quot; ใช่หรือไม่? การเปลี่ยนแปลงจะมีผลกับเว็บไซต์ผู้เล่นภายใน 5
							นาที
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="gap-2">
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmApply}>
							ยืนยัน
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}

function ThemePresetSkeleton() {
	return (
		<Card className="overflow-hidden">
			<Skeleton className="aspect-[3/4] w-full" />
			<CardContent className="p-3">
				<div className="flex items-center justify-between gap-2">
					<Skeleton className="h-4 w-20" />
					<Skeleton className="h-5 w-10" />
				</div>
			</CardContent>
			<CardFooter className="p-3 pt-0">
				<Skeleton className="h-9 w-full" />
			</CardFooter>
		</Card>
	)
}
